import type { ReactNode } from 'react'
import { useEffect, useMemo } from 'react'
import { connect, useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'

import { ThirdParty, type ThirdPartyConfig } from '@dhl/third-party'

import { useGetUserCurrentAnonymous } from '@dhl/receiver-user-service-api'
import { getCarrierByDomain } from '~/util/domain/getCarrierByDomain'
import { type ReduxState } from '~/redux/createRootReducer'
import { selectIsLoggedIn } from '~/account/model/selectors'
import { getConfig, getTopLevelDomain } from '~/config'

type Props = {
  isInitialized: boolean
  children?: ReactNode
}

const {
  adobe: { url: adobeTagManagerUrl },
  cspNonce,
  datadog,
  environment,
  gtm,
  oneTrust,
  version,
} = getConfig()

const StartUpComponent = ({ children, isInitialized }: Props) => {
  const { isLoggedIn } = useSelector(selectIsLoggedIn)
  const { data: userData } = useGetUserCurrentAnonymous({ query: { enabled: isLoggedIn } })

  const config: ThirdPartyConfig = {
    oneTrustId: oneTrust.id,
    gmtId: gtm.id,
    virtualPageRootPath: 'pwa',
    oneTrustNativeEnabled: oneTrust.enableForNative,
    oneTrustNativeEnabledFromVersion: oneTrust.enableForNativeFromVersion,
    isLoggedIn,
    nonce: cspNonce,
    topLevelDomain: getTopLevelDomain(),
    adobeTagManagerUrl,
  }

  const datadogConfig = useMemo(
    () => ({
      environment,
      userId: userData?.user.userId,
      version,
      ...datadog,
    }),
    [userData?.user.userId]
  )

  useEffect(() => {
    const carrierByDomain = getCarrierByDomain()

    if (carrierByDomain) {
      const [, carrier] = carrierByDomain.split('-')
      // eslint-disable-next-line no-unsafe-optional-chaining
      const normalizedCarrier = carrier?.charAt(0).toUpperCase() + carrier?.slice(1).toLowerCase() || ''
      document.title = `My DHL ${normalizedCarrier}`.trim()
    }
  }, [])

  return (
    <ThirdParty config={config} datadogConfig={datadogConfig}>
      {isInitialized ? children : <CircularProgress />}
    </ThirdParty>
  )
}

const mapStateToProps = (state: ReduxState) => ({
  isInitialized: state.config.isInitialized,
})

export const StartUp = connect(mapStateToProps)(StartUpComponent)
